// Step navigation for a react-hook-form wizard.
//
// The important part is that Next validates ONLY the current step's fields. With
// a single resolver over the whole schema, validating everything on Next means a
// field the operator has not reached yet reports an error and blocks progress —
// the classic multi-step failure. `trigger(fields)` scopes it.
//
// One form instance holds all the values, so going Back preserves what was typed
// (with shouldUnregister left at its default false).

import { useCallback, useMemo, useState } from "react";
import type { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

export interface WizardStepDef<TValues extends FieldValues> {
	id: string;
	label: string;
	/**
	 * Fields this step owns. Next validates exactly these. Empty for steps that
	 * only display (a review step, for instance).
	 */
	fields: FieldPath<TValues>[];
	/** Skip this step entirely, e.g. a branch not taken. */
	skip?: boolean;
}

export interface UseWizardResult<TValues extends FieldValues> {
	steps: WizardStepDef<TValues>[];
	currentIndex: number;
	current: WizardStepDef<TValues>;
	isFirst: boolean;
	isLast: boolean;
	/** Validate the current step and advance. Returns false when it did not. */
	next: () => Promise<boolean>;
	back: () => void;
	/** Jump to a step by id. Used by Edit links on a review step. */
	goTo: (id: string) => void;
}

export function useWizard<TValues extends FieldValues>(
	form: UseFormReturn<TValues>,
	allSteps: WizardStepDef<TValues>[],
): UseWizardResult<TValues> {
	// Derived, not stored: a step's visibility can depend on values that change
	// while the wizard is open (picking a target changes which steps apply).
	const steps = useMemo(() => allSteps.filter((s) => !s.skip), [allSteps]);
	const [currentId, setCurrentId] = useState(() => steps[0]?.id ?? "");

	const rawIndex = steps.findIndex((s) => s.id === currentId);
	// If the active step just became skipped, fall back to the first step rather
	// than rendering nothing.
	const currentIndex = rawIndex >= 0 ? rawIndex : 0;
	const current = steps[currentIndex] as WizardStepDef<TValues>;

	const next = useCallback(async () => {
		const step = steps[currentIndex];
		if (!step) return false;
		if (step.fields.length > 0) {
			const ok = await form.trigger(step.fields, { shouldFocus: true });
			if (!ok) return false;
		}
		const following = steps[currentIndex + 1];
		if (!following) return false;
		setCurrentId(following.id);
		return true;
	}, [currentIndex, form, steps]);

	const back = useCallback(() => {
		const previous = steps[currentIndex - 1];
		if (previous) setCurrentId(previous.id);
	}, [currentIndex, steps]);

	const goTo = useCallback(
		(id: string) => {
			if (steps.some((s) => s.id === id)) setCurrentId(id);
		},
		[steps],
	);

	return {
		steps,
		currentIndex,
		current,
		isFirst: currentIndex === 0,
		isLast: currentIndex === steps.length - 1,
		next,
		back,
		goTo,
	};
}
